'use client';

/**
 * ScrollableTabRow — rând de tab-uri derulabil orizontal cu afordanțe de overflow:
 * săgeți ‹ › (afișate DOAR când există conținut de derulat în direcția respectivă)
 * + fade-uri la margini. Autodetecție prin ResizeObserver (redimensionare ecran /
 * schimbare conținut) + listener de scroll. Click pe săgeată derulează ~70% din
 * lățimea vizibilă. Scrollbar-ul nativ e ascuns (afordanța = săgețile + fade).
 *
 * Folosit de bara de tab-uri din /setari (și orice altă bară de nomenclatoare):
 *   <ScrollableTabRow className="<clase bg/border ale rândului>"> {tabs} </ScrollableTabRow>
 */

import { useRef, useState, useEffect, useCallback, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export function ScrollableTabRow({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft < max - 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el); // clientWidth (redimensionare ecran)
      const content = el.firstElementChild;
      if (content) ro.observe(content); // scrollWidth (schimbare conținut)
    }
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [update, children]);

  const scroll = (dir: 1 | -1) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' });
  };

  const arrowCls =
    'absolute top-1/2 -translate-y-1/2 z-20 grid h-6 w-6 place-items-center rounded-full ' +
    'bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 ' +
    'text-neutral-600 dark:text-neutral-300 shadow-sm transition-colors ' +
    'hover:bg-neutral-50 hover:text-primary-600 dark:hover:bg-neutral-700 dark:hover:text-primary-400';

  return (
    <div className="relative">
      {canLeft && (
        <>
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-10 z-10 bg-gradient-to-r from-white dark:from-neutral-900 to-transparent" />
          <button type="button" aria-label="Derulează la stânga" onClick={() => scroll(-1)} className={arrowCls + ' left-0.5'}>
            <ChevronLeft className="h-4 w-4" />
          </button>
        </>
      )}

      <div ref={ref} className={'flex gap-0.5 overflow-x-auto stab-noscroll ' + className}>
        {children}
      </div>

      {canRight && (
        <>
          <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-10 z-10 bg-gradient-to-l from-white dark:from-neutral-900 to-transparent" />
          <button type="button" aria-label="Derulează la dreapta" onClick={() => scroll(1)} className={arrowCls + ' right-0.5'}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}

      <style>{'.stab-noscroll{scrollbar-width:none;-ms-overflow-style:none}.stab-noscroll::-webkit-scrollbar{display:none}'}</style>
    </div>
  );
}
